import { filterClasses } from "@swivl-mono/helpers";
import { Select, SelectOption, SelectSize, Switch, SwitchVariant, Tooltip } from "@swivl-mono/ui";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { PiWarningBold } from "react-icons/pi";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
type Day = (typeof DAYS)[number];

interface DaySchedule {
  day: Day;
  enabled: boolean;
  start: string;
  end: string;
}

interface CallCenterDayHours {
  day: Day;
  hours: string | null;
}

const DEFAULT_WORKING_HOURS: DaySchedule[] = [
  { day: "Mon", enabled: true, start: "9:00 AM", end: "6:00 PM" },
  { day: "Tue", enabled: true, start: "9:00 AM", end: "6:00 PM" },
  { day: "Wed", enabled: true, start: "9:00 AM", end: "6:00 PM" },
  { day: "Thu", enabled: true, start: "9:00 AM", end: "6:00 PM" },
  { day: "Fri", enabled: true, start: "9:00 AM", end: "6:00 PM" },
  { day: "Sat", enabled: false, start: "9:00 AM", end: "2:00 PM" },
  { day: "Sun", enabled: false, start: "9:00 AM", end: "2:00 PM" }
];

const CALL_CENTER_HOURS: CallCenterDayHours[] = [
  { day: "Mon", hours: "9:00 AM - 6:00 PM" },
  { day: "Tue", hours: "9:00 AM - 6:00 PM" },
  { day: "Wed", hours: "9:00 AM - 6:00 PM" },
  { day: "Thu", hours: "9:00 AM - 6:00 PM" },
  { day: "Fri", hours: "9:00 AM - 6:00 PM" },
  { day: "Sat", hours: "9:00 AM - 6:00 PM" },
  { day: "Sun", hours: null }
];

const TIMEZONE_OPTIONS: SelectOption[] = [
  { label: "Pacific Time - Los Angeles (UTC-08:00)", value: "America/Los_Angeles" },
  { label: "Mountain Time - Denver (UTC-07:00)", value: "America/Denver" },
  { label: "Central Time - Chicago (UTC-06:00)", value: "America/Chicago" },
  { label: "Eastern Time - New York (UTC-05:00)", value: "America/New_York" },
  { label: "Alaska Time - Anchorage (UTC-09:00)", value: "America/Anchorage" },
  { label: "Hawaii Time - Honolulu (UTC-10:00)", value: "Pacific/Honolulu" },
  { label: "Atlantic Time - Halifax (UTC-04:00)", value: "America/Halifax" },
  { label: "GMT - London (UTC+00:00)", value: "Europe/London" },
  { label: "Central European - Berlin (UTC+01:00)", value: "Europe/Berlin" },
  { label: "Eastern European - Bucharest (UTC+02:00)", value: "Europe/Bucharest" },
  { label: "India Standard - Kolkata (UTC+05:30)", value: "Asia/Kolkata" },
  { label: "Japan Standard - Tokyo (UTC+09:00)", value: "Asia/Tokyo" },
  { label: "Australia Eastern - Sydney (UTC+10:00)", value: "Australia/Sydney" }
];

function generateTimeOptions(): SelectOption[] {
  const options: SelectOption[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? "AM" : "PM";
      const label = `${h12}:${String(minute).padStart(2, "0")} ${ampm}`;
      options.push({ label, value: label });
    }
  }
  return options;
}

interface CallCenterTimeRange {
  open: string;
  close: string;
}

function getCallCenterTimes(day: Day): CallCenterTimeRange | null {
  const entry = CALL_CENTER_HOURS.find((h) => h.day === day);
  if (!entry?.hours) return null;
  const [open, close] = entry.hours.split(" - ");
  return { open, close };
}

function getWarningMessage(schedule: DaySchedule, ccTimes: CallCenterTimeRange | null): string | null {
  if (!schedule.enabled || !ccTimes) return null;
  const startMin = parseTimeToMinutes(schedule.start);
  const endMin = parseTimeToMinutes(schedule.end);
  const openMin = parseTimeToMinutes(ccTimes.open);
  const closeMin = parseTimeToMinutes(ccTimes.close);
  if (startMin < openMin) {
    return `Schedule starts before call center opens (${ccTimes.open}).`;
  }
  if (endMin > closeMin) {
    return `Schedule ends after call center closes (${ccTimes.close}).`;
  }
  return null;
}

function parseTimeToMinutes(time: string): number {
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "AM" && hours === 12) hours = 0;
  if (period === "PM" && hours !== 12) hours += 12;
  return hours * 60 + minutes;
}

function CallCenterHoursCard(): ReactNode {
  const rows: CallCenterDayHours[][] = [];
  for (let i = 0; i < CALL_CENTER_HOURS.length; i += 3) {
    rows.push(CALL_CENTER_HOURS.slice(i, i + 3));
  }

  return (
    <div className="rounded border border-grey-300 px-4 py-3">
      <div className="border-b border-grey-200 pb-2">
        <span className="heading-base-alt">Call Center Hours (Pacific Time)</span>
      </div>
      <div className="flex flex-col gap-1 pt-2">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex">
            {row.map((entry) => (
              <div key={entry.day} className="flex flex-1 items-center gap-2">
                <span className="body-sm-alt w-[27px] text-center">{entry.day}</span>
                <span className="body-sm text-grey-600">{entry.hours ?? "Closed"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface WorkingHoursRowProps {
  schedule: DaySchedule;
  timeOptions: SelectOption[];
  warning: string | null;
  isLast: boolean;
  nextDisabled: boolean;
  onToggle: (enabled: boolean) => void;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
}

function WorkingHoursRow({
  schedule,
  timeOptions,
  warning,
  isLast,
  nextDisabled,
  onToggle,
  onStartChange,
  onEndChange
}: WorkingHoursRowProps): ReactNode {
  return (
    <>
      <div className={filterClasses("relative flex items-center", warning ? "bg-yellow-50" : schedule.enabled ? "bg-white" : "bg-grey-50")}>
        <div className="flex w-18 items-center justify-center px-3 py-2">
          <Switch on={schedule.enabled} onSet={(on) => onToggle(on)} variant={SwitchVariant.TOGGLE} />
        </div>
        <div className="flex w-21 items-center px-3 py-2">
          <span className={filterClasses("body-sm", schedule.enabled ? "text-black" : "text-grey-500")}>{schedule.day}</span>
        </div>
        <div className="min-w-0 flex-1 py-[7px] pr-1.5 pl-3 [&_div]:!min-w-0">
          <Select
            options={timeOptions}
            selectedValues={[schedule.start]}
            onChange={(values) => onStartChange(values[0])}
            size={SelectSize.SMALL}
            disabled={!schedule.enabled}
            hideClear
          />
        </div>
        <div className="min-w-0 flex-1 py-[7px] pr-3 pl-1.5 [&_div]:!min-w-0">
          <Select
            options={timeOptions}
            selectedValues={[schedule.end]}
            onChange={(values) => onEndChange(values[0])}
            size={SelectSize.SMALL}
            disabled={!schedule.enabled}
            hideClear
          />
        </div>
      </div>
      {warning && (
        <div className="flex items-center gap-1.5 bg-yellow-50 px-4 py-1.5">
          <PiWarningBold className="size-4 shrink-0 text-yellow-700" />
          <span className="body-sm-alt text-yellow-700">{warning}</span>
        </div>
      )}
      {!isLast && !warning && (
        <div className={filterClasses("flex px-3", !schedule.enabled && nextDisabled && "bg-grey-50")}>
          <div className="w-full border-b border-grey-300" />
        </div>
      )}
    </>
  );
}

export function WorkingHoursTab(): ReactNode {
  const [workingHours, setWorkingHours] = useState<DaySchedule[]>(DEFAULT_WORKING_HOURS);
  const [timezone, setTimezone] = useState<string>("America/Los_Angeles");

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const handleToggle = useCallback((day: Day, enabled: boolean): void => {
    setWorkingHours((prev) => prev.map((s) => (s.day === day ? { ...s, enabled } : s)));
    // TODO: API call to save
  }, []);

  const handleStartChange = useCallback((day: Day, value: string): void => {
    setWorkingHours((prev) => prev.map((s) => (s.day === day ? { ...s, start: value } : s)));
    // TODO: API call to save
  }, []);

  const handleEndChange = useCallback((day: Day, value: string): void => {
    setWorkingHours((prev) => prev.map((s) => (s.day === day ? { ...s, end: value } : s)));
    // TODO: API call to save
  }, []);

  function handleTimezoneChange(values: string[]): void {
    setTimezone(values[0]);
    // TODO: API call to save
  }

  return (
    <div className="flex flex-col gap-6">
      <CallCenterHoursCard />

      <div className="flex flex-col gap-2">
        <span className="heading-base-alt">Timezone Setting</span>
        <Select
          options={TIMEZONE_OPTIONS}
          selectedValues={[timezone]}
          onChange={handleTimezoneChange}
          size={SelectSize.MEDIUM}
          hasSearchInDropdown
          hideClear
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <span className="heading-base-alt">Working Hours</span>
          <Tooltip
            content="Configure the hours you are available to handle conversations"
            placement="bottom"
            classNames={{ tooltip: "text-center body-sm !max-w-64" }}
          >
            <IoMdInformationCircleOutline className="size-4 text-grey-600" />
          </Tooltip>
        </div>

        <div className="overflow-hidden rounded border border-grey-300">
          <div className="flex items-center bg-grey-100">
            <div className="w-18 px-3 py-3" />
            <div className="w-21 px-3 py-3">
              <span className="body-mono-alt">Day</span>
            </div>
            <div className="min-w-0 flex-1 px-3 py-3">
              <span className="body-mono-alt">Start</span>
            </div>
            <div className="min-w-0 flex-1 px-3 py-3">
              <span className="body-mono-alt">End</span>
            </div>
          </div>
          <div className="border-b border-grey-300" />

          {workingHours.map((schedule, index) => (
            <WorkingHoursRow
              key={schedule.day}
              schedule={schedule}
              timeOptions={timeOptions}
              warning={getWarningMessage(schedule, getCallCenterTimes(schedule.day))}
              isLast={index === workingHours.length - 1}
              nextDisabled={index < workingHours.length - 1 && !workingHours[index + 1].enabled}
              onToggle={(enabled) => handleToggle(schedule.day, enabled)}
              onStartChange={(value) => handleStartChange(schedule.day, value)}
              onEndChange={(value) => handleEndChange(schedule.day, value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
